<template>
    <div class="absolute top-16 left-0 right-0 z-20 px-3">
        <!-- Sponsor ticker pill -->
        <div class="flex items-stretch gap-2">
            <!-- Rotating sponsor card -->
            <button
                @click="handleTickerClick"
                class="flex-1 min-w-0 bg-black/30 backdrop-blur-md border border-white/10 rounded-full px-3 py-2 flex items-center gap-2.5 cursor-pointer hover:bg-white/10 transition-colors"
            >
                <!-- Avatar -->
                <Transition name="ticker-fade" mode="out-in">
                    <div
                        :key="currentSponsor.name"
                        class="flex items-center gap-2.5 min-w-0 flex-1"
                    >
                        <div
                            class="w-7 h-7 rounded-full shrink-0 flex items-center justify-center"
                            :class="currentIndex === 0 ? 'bg-gradient-to-br from-amber-400 to-amber-600' : 'bg-zinc-600'"
                        >
                            <span class="text-white text-[11px] font-bold">{{ currentSponsor.name?.[0] || '?' }}</span>
                        </div>

                        <div class="min-w-0 flex-1">
                            <p class="text-white text-xs font-semibold truncate">{{ currentSponsor.name }}</p>
                            <p v-if="currentComment" class="text-white/40 text-[10px] truncate leading-tight">"{{ currentComment }}"</p>
                        </div>

                        <div class="flex items-center gap-1 shrink-0">
                            <span class="text-white font-bold text-xs">{{ currentSponsor.amount }}</span>
                            <img src="https://sm.naughtycdn.com/images/landers/prize/NA-Star.png" alt="" class="w-3.5 h-3.5" />
                        </div>
                    </div>
                </Transition>
            </button>

            <!-- View all button -->
            <button
                @click="handleSponsorCountClick"
                class="shrink-0 bg-black/30 backdrop-blur-md border border-white/10 rounded-full px-3 py-2 flex items-center text-white/70 text-[11px] font-medium whitespace-nowrap hover:bg-white/10 transition-colors"
            >
                {{ sponsorCount }} sponsors
            </button>
        </div>
    </div>
</template>

<script setup>
import { computed, ref, watch, onMounted, onBeforeUnmount } from 'vue'
import { trackFathomEvent, getFathomPostPrefix } from "@/Helpers/analytics";

const props = defineProps({
    sponsors: {
        type: Array,
        default: () => []
    },
    sponsorCount: {
        type: Number,
        default: 0
    },
    intervalMs: {
        type: Number,
        default: 3000
    },
    routePrefix: {
        type: String
    }
})

const emit = defineEmits(['viewSponsors'])

const fathomPrefix = computed(() => getFathomPostPrefix(props.routePrefix))

const handleTickerClick = () => {
    trackFathomEvent(`${fathomPrefix.value}_Main_Sponsors_1_Clicked`)
    emit('viewSponsors')
}

const handleSponsorCountClick = () => {
    trackFathomEvent(`${fathomPrefix.value}_Main_Sponsors_2_Clicked`)
    emit('viewSponsors')
}

const currentIndex = ref(0)
let ticker = null

const currentSponsor = ref(props.sponsors[0] || { name: '?', amount: 0 })

const currentComment = computed(() => {
    return currentSponsor.value?.comment?.text || null
})

// Reset when sponsors data changes (e.g. on video swipe)
watch(() => props.sponsors, (newSponsors) => {
    if (newSponsors && newSponsors.length > 0) {
        currentIndex.value = 0
        currentSponsor.value = newSponsors[0]
    }
})

const rotateSponsor = () => {
    if (props.sponsors.length <= 1) return
    currentIndex.value = (currentIndex.value + 1) % props.sponsors.length
    currentSponsor.value = props.sponsors[currentIndex.value]
}

onMounted(() => {
    if (props.sponsors.length > 1) {
        ticker = setInterval(rotateSponsor, props.intervalMs)
    }
})

onBeforeUnmount(() => {
    if (ticker) clearInterval(ticker)
})
</script>

<style scoped>
.ticker-fade-enter-active,
.ticker-fade-leave-active {
    transition: opacity 0.3s ease, transform 0.3s ease;
}

.ticker-fade-enter-from {
    opacity: 0;
    transform: translateY(-6px);
}

.ticker-fade-leave-to {
    opacity: 0;
    transform: translateY(6px);
}
</style>
